import Grid from '@material-ui/core/Grid'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { PropsWithChildren, useContext } from 'react'
import styled from 'styled-components'
import { Listing } from '../../featureListing/featureListing'
import {
  StyledDisclaimerLink,
  StyledSubtitle,
  StyledTitle,
} from '../../featureListing/styledFeatureListing'
import { Link } from '../../link/link'
import { DisclaimerContext } from '../../../context/disclaimerContext'
import {
  DisclaimersFragment,
  ProductFeaturesSubBannerFragment,
} from '@gql'
import { html } from '../../../utils/html'
import { isNotNull, nonNull } from '../../../utils/nonNull'
import { rem } from '../../../utils'

export type FCPRatesFeatureListingProps = {
  data: ProductFeaturesSubBannerFragment
}

const StyledFeaturesListing = styled(Grid)`
  padding: ${props => rem(35, props.theme)} 0 0 0;

  ${props => props.theme.breakpoints.up('md')} {
    padding: ${props => rem(50, props.theme)} 0 0 0;
  }
`

export const FCPRatesFeatureListing: React.FC<PropsWithChildren<FCPRatesFeatureListingProps>> = ({ data }) => {
  const { pageId } = useContext(DisclaimerContext)

  const dynamicData = nonNull(data?.productRates)
  const staticData = nonNull(data?.additionalFeatures)

  const productFeatureHeading = data?.heading

  const hasDynamicData = dynamicData?.[0] && ['rates_default_Entry', 'productRatesCustom_default_Entry'].includes(data?.productRates?.[0]?.__typename || '')
  const hasStaticData = staticData?.[0] && data?.additionalFeatures?.[0]?.__typename === 'additionalFeatures_BlockType'

  if (hasDynamicData || hasStaticData) {
    let disclaimer, listItem, productDetails, dynamicProductRates

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (hasDynamicData) ({ productRates: dynamicProductRates } = data!)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (hasStaticData) ({ listItem, disclaimer, productDetails } = data?.additionalFeatures[0]!)

    const validProductRates = dynamicProductRates && ['rates_default_Entry', 'productRatesCustom_default_Entry'].includes(dynamicProductRates?.[0]?.__typename)
    const rateTitle = hasDynamicData && dynamicData?.[0]?.__typename === 'rates_default_Entry' ? dynamicData?.[0]?.title : undefined
    const purpose = hasDynamicData && dynamicData?.[0]?.__typename === 'rates_default_Entry' ? dynamicData?.[0]?.ratePurpose : undefined
    const paymentType = hasDynamicData && dynamicData?.[0]?.__typename === 'rates_default_Entry' ? dynamicData?.[0]?.rateRepaymentType : undefined
    const rateLvrMaximum = hasDynamicData && dynamicData?.[0]?.__typename === 'rates_default_Entry' ? dynamicData?.[0]?.rateLvrMaximum : undefined
    const rateCategory = hasDynamicData && dynamicData?.[0]?.__typename === 'rates_default_Entry' ? dynamicData?.[0]?.ratesCategory : undefined

    return (
      <StyledFeaturesListing>
        <Listing>
          {(productFeatureHeading || rateTitle || productDetails) &&
            <Listing topRow>
              <StyledTitle variant="h5">{productFeatureHeading}</StyledTitle>
              {rateTitle ? (
                <StyledSubtitle variant="h3">
                  {rateTitle}
                  {paymentType && paymentType in REPAYMENT_TYPES && ` - ${REPAYMENT_TYPES[paymentType]}`}
                  {purpose && purpose in PURPOSE_TYPES && ` - ${PURPOSE_TYPES[purpose]}`}
                  {rateCategory && rateCategory[0]?.slug === 'mortgages' && rateLvrMaximum && <span> &le;{rateLvrMaximum}% LVR </span>}
                  {dynamicProductRates?.[0] &&
                      <Link aria-label={'disclaimer block'} href={`#disclaimer_block_${pageId}`} passHref>
                        <StyledDisclaimerLink>
                          <DisclaimerIcons
                            disclaimers={[
                              ...(dynamicProductRates?.[0]?.homeLoanDisclaimers ?? []),
                              ...(dynamicProductRates?.[0]?.personalLoanDisclaimers ?? []),
                            ]
                            }/>
                        </StyledDisclaimerLink>
                      </Link>}
                </StyledSubtitle>
              ) : (
                <>
                  {productDetails && (
                    <StyledSubtitle variant="h3">
                      {html(productDetails)}
                    </StyledSubtitle>
                  )}
                </>)}
            </Listing>
          }
          {/* @ts-ignore */}
          {(validProductRates || listItem) &&
            <Listing centerRow>
              {(validProductRates && dynamicProductRates?.[0]?.productPrimaryRate) &&
                <Listing
                  centerContent
                  value={dynamicProductRates?.[0].productPrimaryRate}
                  percentage
                  asterisk={false}
                >
                  {dynamicProductRates?.[0].productPrimaryRateType}
                </Listing>}
              {(validProductRates && dynamicProductRates?.[0]?.productComparisonRate) &&
                <Listing
                  centerContent
                  value={dynamicProductRates?.[0].productComparisonRate}
                  percentage
                  asterisk={false}
                >
                  {dynamicProductRates?.[0].productComparisonRateTitle}
                </Listing>}
              {(validProductRates && dynamicProductRates?.[0]?.__typename === 'productRatesCustom_default_Entry' && dynamicProductRates?.[0]?.productBonusRate) &&
                <Listing
                  centerContent
                  value={dynamicProductRates?.[0].productBonusRate}
                  percentage
                  asterisk={false}
                >
                  {dynamicProductRates?.[0].productBonusRateTitle}
                </Listing>}
              {/* @ts-ignore */}
              {hasStaticData && listItem && listItem.map((item, index) => (!item?.itemTitle || item?.__typename !== 'listItem_listItem_BlockType' ? null :
                (
                  !dynamicProductRates ||
                  (dynamicProductRates && dynamicProductRates.length === 0) ||
                  (dynamicProductRates && dynamicProductRates?.length > 0 && index < 2)
                ) &&
                <Listing centerContent key={index} value={item.itemValue as string} asterisk={item.asterisk === true ? true : false}
                  {...item?.symbol ? { percentage: true } : {}}>{item.itemTitle}
                </Listing>
              ))}
            </Listing>
          }
          {(disclaimer?.[0]?.disclaimerContent !== null && (disclaimer && disclaimer?.[0]?.__typename === 'disclaimer_disclaimer_BlockType')) ?
            <Listing bottomRow>
              {disclaimer && disclaimer?.[0]?.__typename === 'disclaimer_disclaimer_BlockType' && (
                <Listing disclaimerContent asterisk={disclaimer?.[0]?.disclaimerAsterisk === true ? true : false}>
                  {disclaimer?.[0]?.disclaimerContent && html(disclaimer?.[0]?.disclaimerContent as string)}
                </Listing>
              )}
            </Listing>
            : null
          }

        </Listing>
      </StyledFeaturesListing>
    )
  }

  return null
}

const DisclaimerIcons: React.VFC<{ disclaimers: Maybe<DisclaimersFragment>[] }> = ({ disclaimers }) => {
  const { iconMap } = useContext(DisclaimerContext)

  const icons = nonNull(disclaimers).map(d => {
    if ((d.__typename === 'homeLoanDisclaimers_default_Entry' || d.__typename === 'personalloandisclaimers_default_Entry') && d.disclaimerId) {
      return (
        <span key={d.disclaimerId}>
          {iconMap.get(d.disclaimerId)}
        </span>
      )
    }

    return null
  }).filter(isNotNull)

  return (
    <>
      {icons}
    </>
  )
}

const REPAYMENT_TYPES = {
  'IO': 'Interest Only',
  'P&I': 'Principal & Interest',
} as const

const PURPOSE_TYPES = {
  'I': 'Investment',
  'OO': 'Owner Occupied',
} as const
